<script setup lang="ts">
import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import DOMPurify from 'dompurify'
import GbButton from '@/components/GbButton.vue'
import WikiText from '@/components/wikitext/WikiText.vue'
import { useUserStore } from '@/authentication/user.store'
import PrintLayoutContainer from '@/print/PrintLayoutContainer.vue'
import PrintPage from '@/print/PrintPage.vue'
import PrintPreparing from '@/print/PrintPreparing.vue'
import PrintPreviewOverlay from '@/print/PrintPreviewOverlay.vue'
import { usePrintPreview } from '@/print/usePrintPreview'
import type { StandardDetail } from '@/standards/standard_types'
import type { AnchorObjective } from './anchor_page_types'

const PANEL_CLASSES = 'rounded-xxs border border-gray-400 p-4'

type PrintedObjective = Pick<AnchorObjective, 'level' | 'title' | 'text'>
type Printer = { name?: string, email?: string }

const props = withDefaults(defineProps<{
  keyStandard?: StandardDetail | null
  title: string
  annualText: string
  goalExampleText?: string
  objectives?: PrintedObjective[]
  studentName: string
}>(), {
  keyStandard: null,
  goalExampleText: '',
  objectives: () => []
})

const emit = defineEmits<{
  close: []
  print: []
}>()

const { user } = storeToRefs(useUserStore())
const { isTransitioning, isTrial, printQuota, printsRemaining, printAndClose } = usePrintPreview(close)

// The user store's state is untyped JavaScript.
const printer = computed(() => user.value as Printer)
const printedAt = new Date().toISOString().split('T').join(' ')
const standardHtml = computed(() => DOMPurify.sanitize(props.keyStandard?.standard_text ?? ''))

function close() {
  emit('close')
}

function printPage() {
  printAndClose(() => emit('print'))
}
</script>

<template>
  <PrintPreviewOverlay :is-initializing="isTransitioning" label="Print preview" @close="close">
    <PrintPreparing v-if="isTransitioning" />

    <PrintLayoutContainer v-else>
      <template #top>
        <div class="container flex">
          <p v-if="isTrial" class="flex-auto pt-1.5 text-right text-gray-700" data-testid="printable-quota">
            {{ printsRemaining }} of {{ printQuota }} prints remaining this month
          </p>
          <div class="ml-auto flex flex-none flex-col gap-2 pl-3">
            <GbButton type="button" gb-theme="bluePrimary" class="w-full justify-center" data-testid="printable-print" @click="printPage">
              <span class="material-icons-round mr-1 text-text-lg" aria-hidden="true">print</span>
              Print
            </GbButton>
            <GbButton type="button" gb-theme="grayOutlineSecondary" class="w-full justify-center" data-testid="printable-cancel" @click="close">
              <span class="material-icons-round mr-1 text-text-lg" aria-hidden="true">close</span>
              Cancel
            </GbButton>
          </div>
        </div>
      </template>

      <template #body>
        <PrintPage class="printed-saved-anchor text-text-md [&_p]:max-w-none print:[&_a]:text-black print:[&_blockquote]:border-0 print:[&_a_.material-icons-round]:hidden">
          <p class="text-text-sm leading-none" data-testid="printable-byline">
            Printed by <b>{{ printer.name }} ({{ printer.email }})</b> from folder <b class="fs-exclude">{{ studentName }}</b> on {{ printedAt }}
          </p>
          <p v-if="isTrial" class="text-text-sm leading-none text-gray-700 print:text-black" data-testid="printable-terms">
            Per our legal Terms of Use, the print feature is intended to facilitate communication and collaboration, and not to be used for
            illicit saving or distribution of proprietary content. <b>{{ printsRemaining }} of {{ printQuota }} prints remaining</b> for this month.
          </p>
          <h1 class="mb-2.5 mt-5 text-display-lg font-medium leading-tight">{{ title }}</h1>

          <template v-if="keyStandard">
            <h2 class="mb-2.5 mt-5 text-text-xl font-bold" data-testid="printable-standard-set">{{ keyStandard.standard_set }}</h2>
            <div :class="PANEL_CLASSES" data-testid="printable-key-standard">
              <h3 class="mb-2.5 mt-5 text-text-md font-regular">
                <b>Standard {{ keyStandard.standard_short_ref }}</b>&nbsp;&nbsp; <i>{{ keyStandard.standard_title }}</i>
              </h3>
              <blockquote v-if="keyStandard.core_purpose" class="mb-2 border-l-4 border-gray-300 px-3 pb-1 pt-0.5 text-text-sm">
                <strong>Core Purpose</strong>
                <p>{{ keyStandard.core_purpose }}</p>
              </blockquote>
              <div v-html="standardHtml" />
            </div>
          </template>

          <h2 class="mb-2.5 mt-5 text-text-xl font-bold">Annual Target</h2>
          <div :class="PANEL_CLASSES" class="fs-exclude" data-testid="printable-annual-target">
            <WikiText :text="annualText" />
            <WikiText v-if="goalExampleText" :text="goalExampleText" />
          </div>

          <template v-if="objectives.length > 0">
            <h3 class="mb-2.5 mt-5 text-text-md font-regular">Quarterly Objectives</h3>
            <div
              v-for="(objective, index) in objectives"
              :key="index"
              :class="PANEL_CLASSES"
              class="fs-exclude mb-2.5"
              data-testid="printable-objective"
            >
              <p class="mb-0 font-bold">{{ objective.level }} Quarter: {{ objective.title }}</p>
              <WikiText :text="objective.text" />
            </div>
          </template>
        </PrintPage>
      </template>
    </PrintLayoutContainer>
  </PrintPreviewOverlay>
</template>

<style scoped>
/* Bootstrap's print rules printed each external link's address after it on Vue 2; strategy links carry an aria-label and printed none. */
@media print {
  .printed-saved-anchor :deep(a[href]:not([aria-label]))::after {
    content: " (" attr(href) ")";
  }
}
</style>
